import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import type { UgotProGrant } from '../../ugotPro/ugotProTypes';
import {
  UGOT_PRO_DEFAULT_GRANT_NOTE,
  UGOT_PRO_GRANT_STATE_LABELS,
  formatUgotProDate,
  formatUgotProDateTime,
  suggestUgotProGrantDate,
} from '../../ugotPro/ugotProFormat';

export type UgotProGrantInput = { note: string; date: string };

type Props = {
  grant: UgotProGrant | null;
  busyKey: string | null;
  onGrant: (input: UgotProGrantInput) => void | Promise<void>;
  onRevoke: () => void | Promise<void>;
  now?: Date;
};

/** 한 사용자의 내부 권한: 지금 상태, 부여·연장(메모·만료일), 회수. */
export function UgotProGrantSection({ grant, busyKey, onGrant, onRevoke, now }: Props) {
  const [note, setNote] = useState(grant?.note || UGOT_PRO_DEFAULT_GRANT_NOTE);
  const [date, setDate] = useState(() => suggestUgotProGrantDate(grant?.active ? grant.expiresAt : null, now));
  const state = grant ? UGOT_PRO_GRANT_STATE_LABELS[grant.state] : null;
  const granting = busyKey === 'grant';
  const revoking = busyKey === 'revoke';

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4" data-testid="ugot-pro-grant-section">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold tracking-tight">내부 권한(결제 없이 UGOT Pro 전체)</h3>
        {state ? <Badge variant={state.tone}>{state.label}</Badge> : <Badge variant="outline">권한 없음</Badge>}
      </div>
      {grant ? (
        <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
          <div><dt className="inline text-muted-foreground">만료 </dt><dd className="inline">{formatUgotProDateTime(grant.expiresAt)}</dd></div>
          <div><dt className="inline text-muted-foreground">메모 </dt><dd className="inline break-all">{grant.note || '-'}</dd></div>
          <div><dt className="inline text-muted-foreground">부여 </dt><dd className="inline break-all">{grant.grantedBy} · {formatUgotProDate(grant.grantedAt)}</dd></div>
          {grant.revokedAt && (
            <div><dt className="inline text-muted-foreground">회수 </dt><dd className="inline break-all">{grant.revokedBy || '-'} · {formatUgotProDateTime(grant.revokedAt)}</dd></div>
          )}
        </dl>
      ) : (
        <p className="text-sm text-muted-foreground">이 사용자에게 준 내부 권한이 없습니다.</p>
      )}

      <form
        className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_170px_auto] sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          void onGrant({ note, date });
        }}
      >
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">메모</span>
          <Input value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} data-testid="ugot-pro-grant-note" />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">만료일</span>
          <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} data-testid="ugot-pro-grant-date" />
        </label>
        <Button type="submit" disabled={granting || revoking || !note.trim()} data-testid="ugot-pro-grant-submit">
          {granting && <Loader2 className="h-4 w-4 animate-spin" />}
          {grant?.active ? '만료일 바꾸기(연장)' : 'UGOT Pro 주기'}
        </Button>
      </form>
      <p className="text-xs text-muted-foreground">
        기본은 오늘부터 1년, 최대 1830일. 다음 상태 조회부터 반영되고 앱은 최대 5분 캐시합니다. 결제 구독은 건드리지 않습니다.
      </p>

      {grant?.active && (
        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={granting || revoking}
            onClick={() => void onRevoke()}
            data-testid="ugot-pro-revoke"
          >
            {revoking && <Loader2 className="h-4 w-4 animate-spin" />}
            권한 회수(지금 끊기)
          </Button>
          <span className="text-xs text-muted-foreground">기록은 남고, 다시 주면 같은 행이 살아납니다.</span>
        </div>
      )}
    </section>
  );
}
